import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEventContext } from "../../context/EventContext";
import EventManager from "./EventManager";

const EventList: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const {
    events,
    loading,
    setEventActive,
    setEventInactive,
    deleteEvent,
    isEventActive,
  } = useEventContext();
  const navigate = useNavigate();

  const handleEventClick = (eventId: string) => {
    navigate(`/admin/event/${eventId}`);
  };

  const handleToggleActive = async (
    eventId: string,
    isActive: boolean,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // Prevent navigation
    try {
      if (isActive) {
        await setEventActive(eventId);
      } else {
        await setEventInactive(eventId);
      }
      // No need to manually fetch - listeners will update the state
    } catch (error) {
      console.error("Error toggling event status:", error);
      alert("Failed to update event status");
    }
  };

  const handleDeleteEvent = async (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation

    if (
      !window.confirm(
        "Are you sure you want to delete this event? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await deleteEvent(eventId);
      // No need to manually fetch - listeners will update the state
      alert("Event deleted successfully");
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Failed to delete event");
    }
  };

  if (showCreateForm) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Create New Event</h2>
          <button
            onClick={() => setShowCreateForm(false)}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Back to List
          </button>
        </div>
        <EventManager
          mode="create"
          onSuccess={() => setShowCreateForm(false)}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-primary text-white p-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold">Events</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-opacity-90"
        >
          Create New Event
        </button>
      </div>
      <div className="p-6">
        {events.length === 0 ? (
          <div className="text-center text-gray-600">
            <p>No events created yet</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90"
            >
              Create Your First Event
            </button>
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <div
                key={event.id}
                onClick={() => handleEventClick(event.id)}
                className="border rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-semibold">{event.title}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) =>
                          handleToggleActive(
                            event.id,
                            !isEventActive(event.id),
                            e
                          )
                        }
                        className={`px-2 py-1 rounded text-sm ${
                          isEventActive(event.id)
                            ? "bg-green-500 text-white hover:bg-green-600"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        {isEventActive(event.id) ? "Active" : "Inactive"}
                      </button>
                      <button
                        onClick={(e) => handleDeleteEvent(event.id, e)}
                        className="px-2 py-1 rounded text-sm bg-red-500 text-white hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    <p>Date: {new Date(event.date).toLocaleDateString()}</p>
                    <p>Time: {event.time}</p>
                    <p>Max Tickets: {event.maxTickets}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventList;
